import { FunctionComponent } from "react";
import { formatCurrencyBRL, formatCurrencyBRL4 } from "../../../utils/format";

export type MoneyTone = "auto" | "up" | "down" | "neutral";

interface MoneyValueProps {
    /** Valor numerico (pode vir como string da API) */
    amount: number | string | null | undefined;
    /** Codigo da moeda (default BRL via formatCurrencyBRL). Quando definido, usa Intl. */
    currency?: string;
    /** Simbolo do asset exibido na frente do valor (ex: "R$", "BTC", "USDT").
     *  Tem precedencia sobre `currency`: simbolos de cripto nao sao codigos ISO
     *  validos e fazem o Intl lancar RangeError (ex: "USDT"). */
    symbol?: string;
    /** Numero de casas decimais. Para BRL 4 casas, passe precision=4.
     *  Quando `symbol` esta definido vira o MAXIMO de casas. */
    precision?: 2 | 4 | 6 | 8;
    /** Minimo de casas decimais quando `symbol` esta definido (default 2).
     *  Cripto usa 6 para nao exibir BTC 0,50 no lugar de BTC 0,500000. */
    minPrecision?: number;
    /** Tom da cor:
     *  - auto: positivo verde, negativo magenta, zero neutro
     *  - up/down/neutral: forca a cor
     */
    tone?: MoneyTone;
    /** Mostra sinal + para positivos */
    showSign?: boolean;
    /** Fallback quando amount eh null/undefined */
    fallback?: string;
    className?: string;
}

const toNumber = (v: number | string | null | undefined): number | null => {
    if (v === null || v === undefined) return null;
    const n = typeof v === "string" ? Number(v) : v;
    return Number.isFinite(n) ? n : null;
};

const MoneyValue: FunctionComponent<MoneyValueProps> = ({
    amount,
    currency,
    symbol,
    precision = 2,
    minPrecision = 2,
    tone = "auto",
    showSign = false,
    fallback = "—",
    className = "",
}) => {
    const n = toNumber(amount);
    if (n === null) return <span className={`num ${className}`.trim()}>{fallback}</span>;

    let label: string;
    if (symbol) {
        // Entre `minPrecision` e `precision` casas. Fiat: 2 e 2 (R$ 1.234,50).
        // Cripto: 6 e 8, garantindo BTC 0,500000 e ainda cabendo BTC 0,00000042.
        // O Math.min evita RangeError caso o minimo passe do maximo.
        label = `${symbol} ${n.toLocaleString("pt-BR", {
            minimumFractionDigits: Math.min(minPrecision, precision),
            maximumFractionDigits: precision,
        })}`;
    } else if (currency && currency !== "BRL") {
        label = n.toLocaleString("pt-BR", {
            style: "currency",
            currency,
            minimumFractionDigits: precision,
            maximumFractionDigits: precision,
        });
    } else if (precision === 4) {
        label = formatCurrencyBRL4(n);
    } else {
        label = formatCurrencyBRL(n);
    }

    if (showSign && n > 0) label = `+${label}`;

    const resolvedTone =
        tone === "auto"
            ? n > 0 ? "up" : n < 0 ? "down" : "neutral"
            : tone;

    const colorClass =
        resolvedTone === "up"   ? "delta-up"
        : resolvedTone === "down" ? "delta-down"
        : "";

    return <span className={`num ${colorClass} ${className}`.trim()}>{label}</span>;
};

export default MoneyValue;
